import React from "react";

import { ButtonBase, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { MealKind } from "@/gql/graphql";
import useFragment from "@/hooks/useFragment";
import { colors } from "@/theme/overrides/colors";

import MealTypeIcon from "./MealTypeIcon";
import { mealKindLabel } from "../utils/mealKindLabel";

interface Props {
  mealId?: string;
  mealType: MealKind;
  active: boolean;
  onClick(): void;
}

const useStyles = makeStyles()({
  root: {
    padding: 15,
    width: "100%",
    backgroundColor: colors.white,
    textAlign: "start",
    transition: "background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  active: {
    backgroundColor: colors.primary,
    color: colors.white,
    "&:hover": {
      backgroundColor: "#12c4a0",
    },
  },
  activeText: {
    color: colors.white,
  },
  container: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  icon: {
    display: "flex",
    alignItems: "center",
  },
  mealType: {
    color: "#363E45",
    fontSize: 14,
    fontWeight: 700,
  },
  caloriesConsumed: {
    color: "#4C515A",
    fontSize: 12,
    fontWeight: 600,
  },
  skippedText: {
    color: "#F27373",
    fontSize: 12,
    fontWeight: 600,
  },
});

const mealTabFragment = graphql(`
  fragment MealTab on Meal {
    id
    mealType
    energyKcal
    skipped
  }
`);

const MealTab: React.FC<Props> = ({ mealId, mealType, active, onClick }) => {
  const { classes, cx } = useStyles();

  const [meal] = useFragment(mealTabFragment, `Meal:${mealId}`);
  const skipped = !!meal?.skipped;

  return (
    <ButtonBase
      className={cx(classes.root, active && classes.active)}
      onClick={onClick}
    >
      <div className={classes.container}>
        <div className={classes.icon}>
          <MealTypeIcon type={mealType} grayscale={skipped} />
        </div>
        <div>
          <Typography
            className={cx(classes.mealType, active && classes.activeText)}
          >
            {mealKindLabel(meal?.mealType ?? mealType)}
          </Typography>
          {skipped ? (
            <Typography className={classes.skippedText}>Skipped</Typography>
          ) : (
            <Typography
              className={cx(
                classes.caloriesConsumed,
                active && classes.activeText
              )}
            >
              Calories consumed: {meal?.energyKcal.toFixed() ?? 0}
            </Typography>
          )}
        </div>
      </div>
    </ButtonBase>
  );
};

export default MealTab;
